import { useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';
import { fetchEmployerLogo } from '@/api/auth';
import { cn } from '@/lib/utils';

interface EmployerLogoProps {
  /** When false, skip fetching and show fallback (or nothing). */
  hasLogo?: boolean;
  employerId?: number | null;
  alt?: string;
  className?: string;
  /** Show Building2 icon when no logo */
  showFallback?: boolean;
  fallbackClassName?: string;
}

export function EmployerLogo({
  hasLogo = false,
  employerId,
  alt = 'Company logo',
  className,
  showFallback = false,
  fallbackClassName,
}: EmployerLogoProps) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!hasLogo || employerId == null) {
      setSrc((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    fetchEmployerLogo(employerId).then((blob) => {
      if (cancelled || !blob) return;
      objectUrl = URL.createObjectURL(blob);
      setSrc((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objectUrl;
      });
    });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };
  }, [hasLogo, employerId]);

  if (src) {
    return (
      <img
        key={employerId ?? 'none'}
        src={src}
        alt={alt}
        className={cn('object-contain', className)}
        draggable={false}
      />
    );
  }

  if (showFallback) {
    return <Building2 className={cn('text-muted-foreground', fallbackClassName ?? className)} />;
  }

  return null;
}
